import { useState, useEffect, useCallback, useMemo } from 'react';
import { PackCard, PresetCard, SearchBar, PackSort } from '../components';
import { usePacks, usePresets } from '../model';
import type { GetPacksParams, IPack } from '../types';
import type { GetPresetsParams, IPreset } from '@/features/presets/types';
import { useToast } from "@/shared/hooks/use-toast";
import { Pagination } from "@/shared/widgets/pagination";
import { useSearchParams } from "react-router-dom";
import { Toggle } from "@/shared/ui/toggle";
import { Check } from "lucide-react";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { NewButton } from "@/shared/widgets/navigation";
import { WithPermission } from "@/shared/widgets/auth/WithPermission";
import { Permission } from "@/shared/types/roles";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useCategoriesData } from "@/features/categories/hooks/useCategoriesData";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { useTranslation } from "@/shared/i18n";
import { formatCoverUrl } from "@/shared/lib/utils";

type SortOption = "name" | "status" | "newest" | "oldest";

const mapSortToApiParams = (sortBy: SortOption): GetPacksParams["sort"] => {
  switch (sortBy) {
    case "name":
      return "name_asc";
    case "status":
      return "published_asc";
    case "newest":
      return "createdAt_desc";
    case "oldest":
      return "createdAt_asc";
    default:
      return "name_asc";
  }
};

const mapSortToPresetApiParams = (sortBy: SortOption): GetPresetsParams["sort"] => {
  switch (sortBy) {
    case "name":
      return "name_asc";
    case "status":
      return "published_asc";
    case "newest":
      return "createdAt_desc";
    case "oldest":
      return "createdAt_asc";
    default:
      return "name_asc";
  }
};

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_DELAY = 500;
const SKELETON_ITEMS = Array.from({ length: 10 }, (_, i) => i);

type ViewMode = "packs" | "presets";

export const PackGrid = () => {
  const { t } = useTranslation('packs');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [publishedOnly, setPublishedOnly] = useState(searchParams.get('published') === 'true');
  const categoryIdParam = searchParams.get('categoryId');
  const [categoryId, setCategoryId] = useState<string | null>(
    categoryIdParam && categoryIdParam !== 'all' ? categoryIdParam : null
  );
  const [isPaid, setIsPaid] = useState<boolean | null>(
    searchParams.get('isPaid') === 'true' ? true : null
  );
  const [viewMode, setViewMode] = useState<ViewMode>(
    (searchParams.get('view') as ViewMode) || 'packs'
  );

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_DELAY);

  // Buscar categorias do tipo PRESET
  const { data: categoriesData } = useCategoriesData({
    type: 'PRESET',
    limit: 100, // Buscar todas as categorias de preset
  });
  const categories = categoriesData?.data || [];

  // Initialize state from URL parameters or use defaults
  const [sortBy, setSortBy] = useState<SortOption>(
    (searchParams.get('sort') as SortOption) || 'oldest'
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get('pageSize') || PAGE_SIZE.toString(), 10)
  );

  // Update URL when state changes (using debounced search)
  useEffect(() => {
    const params = new URLSearchParams();

    if (debouncedSearchQuery) params.set('search', debouncedSearchQuery);
    if (sortBy !== 'oldest') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== PAGE_SIZE) params.set('pageSize', pageSize.toString());
    if (publishedOnly) params.set('published', 'true');
    if (categoryId) params.set('categoryId', categoryId);
    else if (categoryIdParam) params.delete('categoryId'); // Remove from URL if cleared
    if (isPaid !== null) params.set('isPaid', isPaid.toString());
    if (viewMode !== 'packs') params.set('view', viewMode);

    // Only update if there are params to set
    if (Array.from(params.keys()).length > 0) {
      setSearchParams(params, { replace: true });
    } else {
      // If no params, ensure we're at the root URL
      setSearchParams({}, { replace: true });
    }
  }, [debouncedSearchQuery, sortBy, currentPage, pageSize, publishedOnly, categoryId, isPaid, viewMode, setSearchParams]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, sortBy, publishedOnly, categoryId, isPaid, viewMode]);

  // Prepare API parameters (memoized to avoid unnecessary re-renders)
  const apiParams: GetPacksParams = useMemo(() => ({
    name: debouncedSearchQuery || undefined,
    creator: debouncedSearchQuery || undefined,
    sort: mapSortToApiParams(sortBy),
    page: currentPage,
    limit: pageSize,
    published: publishedOnly ? true : undefined,
  }), [debouncedSearchQuery, sortBy, currentPage, pageSize, publishedOnly]);

  const presetApiParams: GetPresetsParams = useMemo(() => ({
    name: debouncedSearchQuery || undefined,
    creator: debouncedSearchQuery || undefined,
    sort: mapSortToPresetApiParams(sortBy),
    page: currentPage,
    limit: pageSize,
    published: publishedOnly ? true : undefined,
    categoryId: categoryId || undefined,
    isPaid: isPaid !== null ? isPaid : undefined,
  }), [debouncedSearchQuery, sortBy, currentPage, pageSize, publishedOnly, categoryId, isPaid]);

  const {
    packs,
    isLoading,
    error,
    totalPages: packsTotalPages,
    totalItems: packsTotalItems,
  } = usePacks(apiParams, { enabled: viewMode === 'packs' });
  const {
    presets,
    isLoading: isLoadingPresets,
    error: presetsError,
    totalPages: presetsTotalPages,
    totalItems: presetsTotalItems,
  } = usePresets(presetApiParams, { enabled: viewMode === 'presets' });

  const totalPages = viewMode === 'presets' ? presetsTotalPages : packsTotalPages;
  const totalItems = viewMode === 'presets' ? presetsTotalItems : packsTotalItems;

  // Memoize handlers to prevent unnecessary re-renders
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(Number(size));
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSortBy(value as SortOption);
  }, []);

  useEffect(() => {
    if (error && viewMode === 'packs') {
      console.error("Error in PackGrid component:", error);
      toast({
        variant: "destructive",
        title: t('errorLoadingPacks'),
        description: `${t('errorDetails')}: ${error instanceof Error ? error.message : t('errorUnknown')
          }`,
      });
    }
  }, [error, toast, viewMode]);

  useEffect(() => {
    if (presetsError && viewMode === 'presets') {
      console.error("Error loading presets:", presetsError);
      toast({
        variant: "destructive",
        title: t('errorLoadingPresets'),
        description: `${t('errorDetails')}: ${presetsError instanceof Error ? presetsError.message : t('errorUnknown')
          }`,
      });
    }
  }, [presetsError, toast, viewMode]);

  // Memoize filters component to avoid re-renders
  const renderFilters = useCallback(() => (
    <div className="flex gap-4 items-center flex-wrap">
      <div className="flex gap-2 border border-white/10 rounded-md p-1">
        <Toggle
          variant="outline"
          aria-label={t('viewPacks')}
          pressed={viewMode === 'packs'}
          onPressedChange={(pressed) => {
            if (pressed) setViewMode('packs');
          }}
          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=off]:bg-transparent"
        >
          {t('packs')}
        </Toggle>
        <Toggle
          variant="outline"
          aria-label={t('viewPresets')}
          pressed={viewMode === 'presets'}
          onPressedChange={(pressed) => {
            if (pressed) setViewMode('presets');
          }}
          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=off]:bg-transparent"
        >
          {t('presets')}
        </Toggle>
      </div>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <PackSort value={sortBy} onChange={handleSortChange} />
      {viewMode === 'presets' && (
        <Select
          value={categoryId || 'all'}
          onValueChange={(value) => setCategoryId(value === 'all' ? null : value)}
        >
          <SelectTrigger className="w-[180px] bg-white/[0.03] border-white/[0.1] text-white">
            <SelectValue placeholder={t('allCategories')} />
          </SelectTrigger>
          <SelectContent className="bg-[#2A2A2A] border-white/[0.1] text-white">
            <SelectItem value="all" className="focus:bg-white/[0.08]">
              {t('allCategories')}
            </SelectItem>
            {categories
              .filter((category) => category.id) // Filtrar categorias sem ID
              .map((category) => (
                <SelectItem
                  key={category.id}
                  value={category.id!}
                  className="focus:bg-white/[0.08]"
                >
                  {category.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )}
      {viewMode === 'presets' && (
        <Toggle
          variant="outline"
          aria-label={t('showPaidOnly')}
          pressed={isPaid === true}
          onPressedChange={(pressed) => setIsPaid(pressed ? true : null)}
          className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
        >
          {isPaid === true && <Check className="mr-2 h-4 w-4" />}
          {t('paid')}
        </Toggle>
      )}
      <Toggle
        variant="outline"
        aria-label={t('showPublishedOnly')}
        pressed={publishedOnly}
        onPressedChange={setPublishedOnly}
        className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
      >
        {publishedOnly && <Check className="mr-2 h-4 w-4" />}
        {t('published')}
      </Toggle>
    </div>
  ), [searchQuery, sortBy, publishedOnly, categoryId, isPaid, viewMode, handleSortChange, categories, t]);

  const isLoadingData = viewMode === 'packs' ? isLoading : isLoadingPresets;
  const pageTitle = viewMode === 'packs' ? t('packs') : t('presets');

  if (isLoadingData) {
    return (
      <PageContent title={pageTitle} items={[{ label: t('home'), href: "/" }]}>
        <div className="space-y-8">
          {renderFilters()}
          <div className="grid grid-cols-5 gap-6 max-md:grid-cols-2">
            {SKELETON_ITEMS.map((index) => (
              <div
                key={index}
                className="flex flex-col overflow-hidden rounded-lg border border-white/[0.06] animate-pulse"
              >
                <div className="relative aspect-[1/1.2] w-full bg-white/5" />
                <div className="flex flex-col gap-3 p-4">
                  <div className="h-6 w-20 rounded bg-white/5" />
                  <div className="space-y-2">
                    <div className="h-4 w-32 rounded bg-white/5" />
                    <div className="h-4 w-24 rounded bg-white/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </PageContent>
    );
  }

  if ((error && viewMode === 'packs') || (presetsError && viewMode === 'presets')) {
    return (
      <PageContent title={pageTitle} items={[{ label: t('home'), href: "/" }]}>
        <div className="space-y-8">
          {renderFilters()}
          <div className="text-center text-white/60 py-8">
            {t('errorLoading', { type: viewMode === 'packs' ? t('packs').toLowerCase() : t('presets').toLowerCase() })}
          </div>
        </div>
      </PageContent>
    );
  }

  const hasNoData = viewMode === 'packs' ? !packs.length : !presets.length;

  if (hasNoData) {
    return (
      <PageContent title={pageTitle} items={[{ label: t('home'), href: "/" }]}>
        <div className="space-y-8">
          {renderFilters()}
          <div className="text-center text-white/60 py-8">
            {searchQuery
              ? t('noResultsFound', { type: viewMode === 'packs' ? t('pack').toLowerCase() : t('preset').toLowerCase() })
              : t('noResults', { type: viewMode === 'packs' ? t('pack').toLowerCase() : t('preset').toLowerCase() })}
          </div>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent
      title={pageTitle}
      items={[{ label: t('home'), href: "/" }]}
      actions={
        viewMode === 'packs' && (
          <WithPermission permissions={[Permission.PACKS_CREATE]}>
            <NewButton path="/packs/new" name={t('pack')} />
          </WithPermission>
        )
      }
    >
      <div className="space-y-8">
        {renderFilters()}

        {/* Renderizar baseado no modo de visualização */}
        {viewMode === 'presets' ? (
          <div className="grid grid-cols-5 gap-6 max-md:grid-cols-2">
            {presets.map((preset) => {
              const creatorData = preset.pack?.packsCreators?.[0]?.creators;
              const creator = creatorData ? {
                name: creatorData.name,
                avatar: creatorData.avatar || "/placeholder.svg"
              } : undefined;
              const packName = preset.pack?.name;
              return (
                <PresetCard
                  key={preset.id}
                  preset={preset}
                  cover={preset.cover || "/placeholder.svg"}
                  published={preset.published}
                  name={preset.name}
                  creator={creator}
                  packName={packName}
                  isFree={preset.isFree}
                />
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-6 max-md:grid-cols-2">

            {packs.map((pack) => {
              const cover = formatCoverUrl(pack.cover);
              const creatorName = pack.packsCreators?.[0]?.creators?.name || "Nome não disponível";
              return (
                <PackCard
                  key={pack.id}
                  pack={pack}
                  cover={cover}
                  published={pack.published}
                  name={pack.name}
                  creator={creatorName}
                  freePresetsCount={pack.freePresetsCount}
                  paidPresetsCount={pack.paidPresetsCount}
                />
              );
            })}
          </div>
        )}

        <div className="mt-6 w-full">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            setCurrentPage={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeOptions={true}
            pageSizeOptions={[10, 20, 50, 100]}
            showTotalItems={false}
          />
        </div>
      </div>
    </PageContent>
  );
};
